import { API_BASE_URL } from '../config/api';

export interface PixelsTrialSummary {
  /** False for pre-existing users who never had a trial. */
  active?: boolean;
  daysRemaining?: number | null;
}

export interface PixelsSubscriptionSummary {
  /** True while the subscription grants access (flips trial off). */
  active?: boolean;
  status?: string | null;
  plan?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
}

export interface PixelsAccessSummary {
  /**
   * Enforcement source of truth (same logic as the backend 403 gate).
   * `via` is "trial" | "subscription" | null.
   */
  allowed?: boolean;
  via?: string | null;
}

export interface PixelsSummary {
  /** Current spendable credit balance; null when the backend omits it. */
  available: number | null;
  trial: PixelsTrialSummary | null;
  subscription: PixelsSubscriptionSummary | null;
  access: PixelsAccessSummary | null;
}

const toFiniteNumberOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const toStringOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

/**
 * Minimal client for GET /pixels/summary. Parses defensively (the backend may
 * omit fields); callers treat `available === null` as "unknown balance" and
 * fall back to existing behavior.
 */
export const getPixelsSummary = async (
  token: string
): Promise<PixelsSummary> => {
  const response = await fetch(`${API_BASE_URL}/pixels/summary`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to load credit balance');
  }

  const parsed: any = await response.json();
  const available =
    toFiniteNumberOrNull(parsed?.available) ??
    toFiniteNumberOrNull(parsed?.balance) ??
    toFiniteNumberOrNull(parsed?.pixels) ??
    null;

  const trialRaw = parsed?.trial;
  const trial: PixelsTrialSummary | null =
    trialRaw && typeof trialRaw === 'object'
      ? {
          ...(typeof trialRaw.active === 'boolean'
            ? { active: trialRaw.active }
            : {}),
          daysRemaining:
            toFiniteNumberOrNull(trialRaw.daysRemaining) ??
            toFiniteNumberOrNull(trialRaw.daysLeft),
        }
      : null;

  const subscriptionRaw = parsed?.subscription;
  const subscription: PixelsSubscriptionSummary | null =
    subscriptionRaw && typeof subscriptionRaw === 'object'
      ? {
          ...(typeof subscriptionRaw.active === 'boolean'
            ? { active: subscriptionRaw.active }
            : {}),
          ...(toStringOrNull(subscriptionRaw.status) !== null
            ? { status: toStringOrNull(subscriptionRaw.status) }
            : {}),
          ...(toStringOrNull(subscriptionRaw.plan) !== null
            ? { plan: toStringOrNull(subscriptionRaw.plan) }
            : {}),
          ...(toStringOrNull(subscriptionRaw.currentPeriodStart) !== null
            ? {
                currentPeriodStart: toStringOrNull(
                  subscriptionRaw.currentPeriodStart
                ),
              }
            : {}),
          ...(toStringOrNull(subscriptionRaw.currentPeriodEnd) !== null
            ? {
                currentPeriodEnd: toStringOrNull(
                  subscriptionRaw.currentPeriodEnd
                ),
              }
            : {}),
          ...(typeof subscriptionRaw.cancelAtPeriodEnd === 'boolean'
            ? { cancelAtPeriodEnd: subscriptionRaw.cancelAtPeriodEnd }
            : {}),
        }
      : null;

  const accessRaw = parsed?.access;
  const access: PixelsAccessSummary | null =
    accessRaw && typeof accessRaw === 'object'
      ? {
          ...(typeof accessRaw.allowed === 'boolean'
            ? { allowed: accessRaw.allowed }
            : {}),
          ...(toStringOrNull(accessRaw.via) !== null
            ? { via: toStringOrNull(accessRaw.via) }
            : {}),
        }
      : null;

  return { available, trial, subscription, access };
};
